/**
 * Arithmetic and trigonometry. AutoLISP promotion rules: INT op INT → INT
 * (division truncates toward zero); any REAL contaminates the result to REAL.
 */

import { LispError } from '../errors';
import { NIL, mkInt, mkNumber, mkReal, type LispRuntimeApi, type LispValue } from '../values';
import { define, expectIntValueOnly, expectNumber } from './internal';

type Num = { k: 'int' | 'real'; v: number };

function asNum(v: LispValue, fn: string): Num {
    if (v.k !== 'int' && v.k !== 'real') throw new LispError(`${fn}: bad argument type`);
    return v;
}

function combine(_fn: string, out: Num): LispValue {
    return out.k === 'int' ? mkInt(out.v) : mkReal(out.v);
}

function fold(
    fn: string,
    args: LispValue[],
    identity: number,
    op: (a: number, b: number) => number,
    isDivision = false
): LispValue {
    if (args.length === 0) return mkNumber(identity);
    const nums = args.map((a) => asNum(a, fn));
    let anyReal = nums.some((n) => n.k === 'real');
    let acc = nums[0].v;
    if (nums.length === 1 && isDivision) {
        acc = 1 / nums[0].v;
        anyReal = true; // (/ x) is always real
    } else {
        for (let i = 1; i < nums.length; i++) {
            const b = nums[i].v;
            if (isDivision && b === 0) throw new LispError('divide by zero');
            acc = op(acc, b);
        }
        // Integer division truncates toward zero (AutoLISP (/ 7 2) → 3).
        if (isDivision && !anyReal) acc = Math.trunc(acc);
    }
    return combine(fn, { k: anyReal ? 'real' : 'int', v: acc });
}

export function registerMath(rt: LispRuntimeApi): void {
    define(rt, '+', (args) => fold('+', args, 0, (a, b) => a + b));
    define(rt, '-', (args) => {
        if (args.length === 0) throw new LispError('too few arguments');
        if (args.length === 1) {
            const n = asNum(args[0], '-');
            return combine('-', { k: n.k, v: -n.v });
        }
        return fold('-', args, 0, (a, b) => a - b);
    });
    define(rt, '*', (args) => fold('*', args, 1, (a, b) => a * b));
    define(rt, '/', (args) => {
        if (args.length === 0) throw new LispError('too few arguments');
        return fold('/', args, 1, (a, b) => a / b, true);
    });
    define(rt, '1+', (args) => {
        const n = asNum(args[0] ?? NIL, '1+');
        return combine('1+', { k: n.k, v: n.v + 1 });
    });
    define(rt, '1-', (args) => {
        const n = asNum(args[0] ?? NIL, '1-');
        return combine('1-', { k: n.k, v: n.v - 1 });
    });
    define(rt, 'rem', (args) => {
        if (args.length < 2) throw new LispError('too few arguments');
        let acc = expectIntValueOnly(args[0], 'rem');
        for (let i = 1; i < args.length; i++) {
            const b = expectIntValueOnly(args[i], 'rem');
            if (b === 0) throw new LispError('divide by zero');
            acc = acc % b;
        }
        return mkInt(acc);
    });
    define(rt, 'mod', (args) => {
        if (args.length !== 2) throw new LispError('syntax error');
        const a = expectNumber(args[0], 'mod');
        const b = expectNumber(args[1], 'mod');
        if (b === 0) throw new LispError('divide by zero');
        return mkNumber(a % b);
    });
    define(rt, 'min', (args) => {
        if (args.length === 0) throw new LispError('too few arguments');
        let best = asNum(args[0], 'min');
        for (const a of args.slice(1)) {
            const n = asNum(a, 'min');
            if (n.v < best.v) best = n;
        }
        return combine('min', best);
    });
    define(rt, 'max', (args) => {
        if (args.length === 0) throw new LispError('too few arguments');
        let best = asNum(args[0], 'max');
        for (const a of args.slice(1)) {
            const n = asNum(a, 'max');
            if (n.v > best.v) best = n;
        }
        return combine('max', best);
    });
    define(rt, 'abs', (args) => {
        const n = asNum(args[0] ?? NIL, 'abs');
        return combine('abs', { k: n.k, v: Math.abs(n.v) });
    });
    define(rt, 'sin', (args) => mkReal(Math.sin(expectNumber(args[0] ?? NIL, 'sin'))));
    define(rt, 'cos', (args) => mkReal(Math.cos(expectNumber(args[0] ?? NIL, 'cos'))));
    define(rt, 'tan', (args) => mkReal(Math.tan(expectNumber(args[0] ?? NIL, 'tan'))));
    define(rt, 'atan', (args) => {
        if (args.length === 1) return mkReal(Math.atan(expectNumber(args[0], 'atan')));
        if (args.length === 2) {
            const a = expectNumber(args[0], 'atan');
            const b = expectNumber(args[1], 'atan');
            if (a === 0 && b === 0) throw new LispError('divide by zero');
            return mkReal(Math.atan2(a, b));
        }
        throw new LispError('too many arguments');
    });
    define(rt, 'sqrt', (args) => {
        const v = expectNumber(args[0] ?? NIL, 'sqrt');
        if (v < 0) throw new LispError('negative argument');
        return mkReal(Math.sqrt(v));
    });
    define(rt, 'exp', (args) => mkReal(Math.exp(expectNumber(args[0] ?? NIL, 'exp'))));
    define(rt, 'log', (args) => {
        const v = expectNumber(args[0] ?? NIL, 'log');
        if (v <= 0) throw new LispError('bad argument');
        return mkReal(Math.log(v));
    });
    define(rt, 'expt', (args) => {
        const base = expectNumber(args[0] ?? NIL, 'expt');
        const power = expectNumber(args[1] ?? NIL, 'expt');
        return mkNumber(Math.pow(base, power));
    });
    define(rt, 'gcd', (args) => {
        if (args.length !== 2) throw new LispError('syntax error');
        let a = Math.abs(expectIntValueOnly(args[0], 'gcd'));
        let b = Math.abs(expectIntValueOnly(args[1], 'gcd'));
        while (b) {
            [a, b] = [b, a % b];
        }
        return mkInt(a);
    });
}
